import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Phone, MapPin, Camera, LogOut, Loader2, AlertTriangle, Save, Eye, EyeOff, Menu, X, Car, Settings, Trash } from 'lucide-react';
import { supabase } from '../lib/supabase';
import UserDashboard from '../components/UserDashboard';

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profile, setProfile] = useState<{
    full_name: string;
    phone: string;
    avatar_url: string | null;
    share_location: boolean;
    latitude: number | null;
    longitude: number | null;
    last_location_update: string | null;
  }>({
    full_name: '',
    phone: '',
    avatar_url: null,
    share_location: false,
    latitude: null,
    longitude: null,
    last_location_update: null
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/giris');
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (profile) {
        setProfile(profile);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Profil yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/giris');
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Çıkış yapılırken bir hata oluştu.');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Kullanıcı bulunamadı');

      // Profili güncelle
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          full_name: profile.full_name,
          phone: profile.phone
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess('Profil başarıyla güncellendi');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Profil güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const toggleLocationSharing = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Kullanıcı bulunamadı');

      // Konum paylaşımını aç/kapat
      await supabase.rpc('toggle_location_sharing', {
        user_id: user.id,
        share: !profile.share_location
      });

      // Konum paylaşımı açıldıysa konum bilgisini güncelle
      if (!profile.share_location) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });

          await supabase.rpc('update_user_location', {
            user_id: user.id,
            lat: position.coords.latitude,
            long: position.coords.longitude
          });
        } catch (locationError) {
          console.error('Konum alınamadı:', locationError);
          throw new Error('Konum alınamadı. Lütfen konum iznini kontrol edin.');
        }
      }

      setProfile(prev => ({ ...prev, share_location: !prev.share_location }));
      setSuccess('Konum paylaşım ayarları güncellendi');
    } catch (err) {
      console.error('Error toggling location sharing:', err);
      setError(err instanceof Error ? err.message : 'Konum paylaşım ayarları güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Kullanıcı bulunamadı');

      // Profili silindi olarak işaretle (gerçekten silmek yerine)
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        // Eğer is_deleted alanı yoksa, bu durumu ele al
        console.error('Update error:', updateError);
        
        // Alternatif olarak, profil verisini temizle
        const { error: cleanError } = await supabase
          .from('user_profiles')
          .update({
            full_name: '[Silinmiş Hesap]',
            phone: '',
            avatar_url: null,
            share_location: false,
            latitude: null,
            longitude: null
          })
          .eq('id', user.id);
          
        if (cleanError) throw cleanError;
      }

      // Çıkış yap ve giriş sayfasına yönlendir
      await supabase.auth.signOut();
      navigate('/giris', { replace: true });
      
      // Kullanıcıya hesabın devre dışı bırakıldığını bildir
      setSuccess('Hesabınız başarıyla devre dışı bırakıldı.');
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Hesap silinirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleMobileNavClick = (path: string) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1d24] flex items-center justify-center">
        <div className="bg-[#24272e] rounded-2xl p-8 shadow-xl border border-white/5">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
            <span className="text-white">Profil Yükleniyor...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#1a1d24]">
      <main className="flex-1 container mx-auto px-2 sm:px-4 py-8 pb-24 md:pb-8">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Dashboard */}
            <UserDashboard />

            {/* Profil Ayarları */}
            <div className="mt-8">
              <div className="bg-[#24272e] rounded-2xl p-6 sm:p-8 shadow-xl border border-white/5">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl shadow-lg shadow-yellow-400/20">
                      <User className="w-6 h-6 text-gray-900" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Profil Ayarları</h2>
                      <p className="text-gray-400">Hesap bilgilerinizi yönetin</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-2 hover:bg-[#2d3039] rounded-lg transition-colors group"
                  >
                    <LogOut className="w-6 h-6 text-gray-400 group-hover:text-white" />
                  </button>
                </div>

                {/* Form */}
                <div className="space-y-6">
                  {/* Ad Soyad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      Ad Soyad
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={profile.full_name}
                        onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                        className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
                        placeholder="Adınız Soyadınız"
                      />
                      <User className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
                    </div>
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">
                      Telefon
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="w-full bg-[#1a1d24] text-white px-4 py-3 rounded-xl border border-white/10 focus:border-yellow-400/50 focus:ring-2 focus:ring-yellow-400/20 focus:outline-none placeholder-gray-500 pl-11"
                        placeholder="(5XX) XXX XX XX"
                      />
                      <Phone className="w-5 h-5 text-gray-400 absolute left-4 top-3.5" />
                    </div>
                  </div>

                  {/* Konum Paylaşımı */}
                  <div className="bg-[#1a1d24] rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-gray-400" />
                        <div>
                          <h3 className="text-white font-medium">Konum Paylaşımı</h3>
                          <p className="text-sm text-gray-400">
                            {profile.share_location ? 'Konumunuz paylaşılıyor' : 'Konumunuz gizli'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={toggleLocationSharing}
                        disabled={saving}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          profile.share_location ? 'bg-yellow-400' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            profile.share_location ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {profile.share_location && profile.last_location_update && (
                      <div className="mt-3 text-sm text-gray-400">
                        Son güncelleme: {new Date(profile.last_location_update).toLocaleString('tr-TR')}
                      </div>
                    )}
                  </div>

                  {/* Hata ve Başarı Mesajları */}
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  {success && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-green-400 text-sm">
                      {success}
                    </div>
                  )}

                  {/* Kaydet Butonu */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Kaydediliyor...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          <span>Kaydet</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Hesap Silme Bölümü */}
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white">Hesabımı Devre Dışı Bırak</h3>
                        <p className="text-sm text-gray-400 mt-1">
                          Bu işlem hesabınızı devre dışı bırakır ve kişisel verileriniz gizlenir.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      >
                        <Trash className="w-4 h-4" />
                        <span>Hesabı Kapat</span>
                      </button>
                    </div>
                  </div>

                  {/* Hesap Silme Onay Modalı */}
                  {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                      <div className="bg-[#24272e] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-xl">
                        <div className="flex items-center gap-3 text-red-400 mb-4">
                          <AlertTriangle className="w-6 h-6" />
                          <h3 className="text-xl font-bold">Hesabınızı Devre Dışı Bırakmak İstiyor musunuz?</h3>
                        </div>
                        
                        <p className="text-gray-300 mb-6">
                          Hesabınızı devre dışı bıraktığınızda:
                        </p>
                        
                        <ul className="list-disc list-inside text-gray-400 space-y-2 mb-6">
                          <li>Profiliniz görünmez olacak</li>
                          <li>Uygulamaya tekrar giriş yapamayacaksınız</li>
                          <li>İleride yönetici onayı ile hesabınız tamamen silinebilir</li>
                        </ul>
                        
                        <div className="flex gap-3 justify-end">
                          <button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="px-4 py-2 bg-[#1a1d24] hover:bg-[#2a2e36] text-white rounded-lg transition-colors"
                          >
                            İptal
                          </button>
                          <button
                            onClick={handleDeleteAccount}
                            disabled={deleting}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            {deleting ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Devre Dışı Bırakılıyor...</span>
                              </>
                            ) : (
                              <>
                                <Trash className="w-4 h-4" />
                                <span>Evet, Hesabımı Kapat</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}